import axio from "axios";
import qs from "qs";
import router from "@/router/index";
import { TOKEN_CONFIG, isTokenExpiredError } from "./tokenConfig";

const headers = {
  "Content-Type": "application/json;charset=utf-8",
  Accept: "application/json",
};

// Token管理相关变量
let isRefreshing = false; // 是否正在刷新token
let failedQueue = []; // 失败请求队列

// 处理队列中的请求
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// 自动重新登录函数
const autoReLogin = async () => {
  try {
    
    const params = TOKEN_CONFIG.AUTO_LOGIN_CREDENTIALS;
    
    // 直接调用登录API，不经过拦截器
    const response = await axio.post(
      "/login",
      params,
      { headers }
    );
    
    if (response.data && response.data.code === 200) {
      const responseData = response.data.data;
      let newToken, newRefreshToken;
      
      // 处理不同的响应格式
      if (typeof responseData === 'object' && responseData.token) {
        newToken = responseData.token;
        newRefreshToken = responseData.refreshToken;
      } else {
        newToken = responseData;
      }
      
      // 存储新token
      localStorage.setItem(TOKEN_CONFIG.TOKEN_KEY, newToken);
      if (newRefreshToken) {
        localStorage.setItem(TOKEN_CONFIG.REFRESH_TOKEN_KEY, newRefreshToken);
      }
      
      return newToken;
    } else {
      throw new Error((response.data && response.data.msg) || "重新登录失败");
    }
  } catch (error) {
    // 清除失效的token
    localStorage.removeItem(TOKEN_CONFIG.TOKEN_KEY);
    localStorage.removeItem(TOKEN_CONFIG.REFRESH_TOKEN_KEY);
    throw error;
  }
};

// 刷新token函数
const refreshToken = async () => {
  try {
    const refreshTokenValue = localStorage.getItem(TOKEN_CONFIG.REFRESH_TOKEN_KEY);
    
    // 如果没有refresh token或者未启用refresh token，直接重新登录
    if (!TOKEN_CONFIG.ENABLE_REFRESH_TOKEN || !refreshTokenValue) {
      if (TOKEN_CONFIG.ENABLE_AUTO_RELOGIN) {
        return await autoReLogin();
      } else {
        throw new Error("没有refresh token且未启用自动重新登录");
      }
    }
    
    // 尝试使用refresh token刷新
    const response = await axio.post(
      `/api${TOKEN_CONFIG.REFRESH_TOKEN_ENDPOINT}`,
      { refreshToken: refreshTokenValue },
      { headers }
    );
    
    if (response.data && response.data.code === 200) {
      const responseData = response.data.data;
      const newToken = responseData.token || responseData;
      const newRefreshToken = responseData.refreshToken;
      
      localStorage.setItem(TOKEN_CONFIG.TOKEN_KEY, newToken);
      if (newRefreshToken) {
        localStorage.setItem(TOKEN_CONFIG.REFRESH_TOKEN_KEY, newRefreshToken);
      }
      
      return newToken;
    } else {
      throw new Error((response.data && response.data.msg) || "Token刷新失败");
    }
  } catch (error) {
    
    // refresh token失效，尝试重新登录
    if (TOKEN_CONFIG.ENABLE_AUTO_RELOGIN) {
      return await autoReLogin();
    } else {
      throw error;
    }
  }
};

const axios = axio.create({
  baseURL: "/api",
  headers,
  timeout: 30 * 1000,
});

// axios请求拦截器
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_CONFIG.TOKEN_KEY);
    if (token) {
      config.headers["token"] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// axios响应拦截器
axios.interceptors.response.use(
  async (res) => {
    return Promise.resolve(res.data);
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 检查是否是token过期错误
    if (isTokenExpiredError(error) && !originalRequest._retry) {
      
      // 如果已经在刷新token，将请求加入队列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          originalRequest.headers['token'] = token;
          return axios(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        
        // 处理队列中的请求
        processQueue(null, newToken);
        
        // 重新设置原始请求的token
        originalRequest.headers['token'] = newToken;
        
        // 显示用户通知（如果启用）
        if (TOKEN_CONFIG.SHOW_USER_NOTIFICATIONS && window.Vue && window.Vue.prototype.$message) {
          window.Vue.prototype.$message.success("登录状态已自动更新");
        }
        
        // 重新发送原始请求
        return axios(originalRequest);
        
      } catch (refreshError) {
        
        // 处理队列中的请求
        processQueue(refreshError, null);
        
        // 清除失效的token
        localStorage.removeItem(TOKEN_CONFIG.TOKEN_KEY);
        localStorage.removeItem(TOKEN_CONFIG.REFRESH_TOKEN_KEY);
        
        // 显示错误通知（如果启用）
        if (TOKEN_CONFIG.SHOW_USER_NOTIFICATIONS && window.Vue && window.Vue.prototype.$message) {
          window.Vue.prototype.$message.error("登录状态已失效，请刷新页面");
        }
        
        // 跳转到登录页（如果启用）
        if (TOKEN_CONFIG.REDIRECT_TO_LOGIN_ON_FAILURE && router) {
          router.push(TOKEN_CONFIG.LOGIN_ROUTE);
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 其他错误直接抛出
    return Promise.reject(error);
  }
);

const request = async (method, url, data, header = headers) => {
  try {
    method = method.toLocaleLowerCase();
    if (method === "post") {
      return await axios.post(url, data, {
        headers: header,
      });
    }
    if (method === "get") {
      return await axios.get(url, {
        params: data,
      });
    }
    if (method === "query") {
      return await axios.post(`${url}?${qs.stringify(data)}`);
    }
    return " ";
  } catch (err) {
    // 如果是网络错误或其他错误，可以在这里统一处理
    if (TOKEN_CONFIG.ENABLE_DEBUG_LOGS) {
    }
    return err;
  }
};

export default request;



// WEBPACK FOOTER //
// ./src/utils/request.js